/**
 * Speaking interface component.
 *
 * Main component for the speaking exam module. Provides a full-screen interface
 * with preparation timer, recording timer, and question progression.
 *
 * @module SpeakingInterface
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { SpeakingWaveform } from "./SpeakingWaveform";
import { AdaptiveQuestionStateBadge } from "./AdaptiveQuestionStateBadge";
import { MicSetupScreen } from "./MicSetupScreen";
import { LiveConversationInterface } from "./LiveConversationInterface";
import { cn } from "@/lib/utils";
import type {
  CEFRLevel,
  ExamType,
  SimulationSettings,
} from "@/types/exam-simulation";
import { useSpeakingModuleState } from "@/hooks/useSpeakingModuleState";
import { useAudioAnalyser } from "@/hooks/useAudioAnalyser";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { buildMediaSource } from "@/utils/examSimulation";
import {
  getModeColor,
  getModeLabel,
  getModeBadgeClassName,
  type SpeakingMode,
} from "./SpeakingInterface.helpers";
import { QuestionContent, TimerDisplay } from "./SpeakingInterface.components";
import { ActionButtons } from "./SpeakingInterface.actions";
import { useSimulationInactivityMonitor } from "@/hooks/useSimulationInactivityMonitor";
import { SIMULATION_INACTIVITY_TIMEOUT_MS } from "@/lib/simulationRuntimeConfig";
import { SimulationInactivityDialog } from "@/components/exam/SimulationInactivityDialog";
import { useConsentStatus } from "@/hooks/useConsentStatus";

interface SpeakingInterfaceProps {
  readonly examType: ExamType;
  readonly level: CEFRLevel;
  readonly settings?: SimulationSettings | null;
  readonly onModuleFinish?: () => void;
  /** Session ID shared across all modules in a simulation */
  readonly sessionId?: string | null;
}

export function SpeakingInterface({
  examType,
  level,
  settings,
  onModuleFinish,
  sessionId,
}: SpeakingInterfaceProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    hasVoiceConsent,
    isLoading: isConsentLoading,
    isError: isConsentError,
  } = useConsentStatus();
  const voiceConsentDenied =
    isConsentLoading || isConsentError || !hasVoiceConsent;

  const {
    currentQuestion,
    totalQuestions,
    state,
    sessionId: resolvedSessionId,
    isLoadingData,
    dataError,
    handleStartEarly,
    handleStopRecording,
    handleFinishModule,
    handleRetrySave,
    handleConversationComplete,
    completeMicSetup,
    requestMicrophone,
  } = useSpeakingModuleState({
    examType,
    level,
    settings: settings ?? null,
    userId: user?.id,
    onModuleFinish,
    sessionId,
    voiceConsentDenied,
  });

  const mode: SpeakingMode = state.mode;

  // Drive the waveform with real audio: the mic stream while recording, or the
  // playing section/context audio element during audio playback.
  const isAudioPlaybackMode =
    mode === "section-audio" || mode === "context-audio";
  const { readInto: readAudioLevels } = useAudioAnalyser({
    stream: mode === "recording" ? state.micStream : null,
    mediaElement: isAudioPlaybackMode ? state.activeAudioElement : null,
    enabled: mode === "recording" || isAudioPlaybackMode,
  });

  const inactivity = useSimulationInactivityMonitor({
    enabled: !state.isSaving && totalQuestions > 0,
    timeoutMs: SIMULATION_INACTIVITY_TIMEOUT_MS,
  });
  const inactivityTimeRemaining = useMemo(() => {
    if (mode === "prep") {
      return state.prepTimeLeft;
    }
    if (mode === "recording") {
      return state.recordTimeLeft;
    }
    return null;
  }, [mode, state.prepTimeLeft, state.recordTimeLeft]);

  const questionLabel = useMemo(() => {
    if (!currentQuestion) return "Question";
    return t("simulation.active.questionOf", {
      current: currentQuestion.displayId,
      total: totalQuestions,
      defaultValue: `Question ${currentQuestion.displayId} of ${totalQuestions}`,
    });
  }, [currentQuestion, totalQuestions, t]);

  const imageSrc = useMemo(() => {
    if (!currentQuestion) return null;
    if (
      currentQuestion.contextImage &&
      (currentQuestion.contextImage.url || currentQuestion.contextImage.binary)
    ) {
      return buildMediaSource(
        currentQuestion.contextImage.url || null,
        currentQuestion.contextImage.binary || null,
        currentQuestion.contextImage.mimeType || undefined,
        "image/jpeg",
      );
    }
    if (currentQuestion.image) {
      return currentQuestion.image;
    }
    return null;
  }, [currentQuestion]);

  const contextText = currentQuestion?.contextText || null;

  // Mic setup screen is shown before the first question — data may still be loading
  if (state.mode === "mic-setup") {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-muted/50 p-3">
        <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
        <div className="relative z-10 w-full max-w-md rounded-2xl border border-border/60 bg-card p-4 shadow-2xl sm:p-8">
          <MicSetupScreen
            permissionStatus={state.permissionStatus}
            onContinue={completeMicSetup}
            onSkip={completeMicSetup}
            onRequestPermission={requestMicrophone}
          />
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-muted/50 p-3">
        <Card className="p-4 text-center sm:p-6">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">
            {t("simulation.errors.loadingModule", {
              module: "Speaking",
              defaultValue: "Loading speaking module...",
            })}
          </p>
        </Card>
      </div>
    );
  }

  if (dataError || !currentQuestion) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-muted/50 p-3">
        <Card className="p-4 text-center sm:p-6">
          <p className="text-destructive font-semibold mb-2">
            {t("simulation.errors.loadFailure", {
              moduleName: "Speaking",
              attempts: 1,
              errorMessage: "Unknown",
              defaultValue: "Failed to load speaking questions.",
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {dataError instanceof Error
              ? dataError.message
              : t("simulation.errors.tryAgain", {
                  defaultValue: "Please try again.",
                })}
          </p>
        </Card>
      </div>
    );
  }

  // Live-conversation tasks (LCT-001) run a dedicated AI-led dialogue flow
  // instead of prep → recording; every other mode/question is unchanged.
  if (mode === "live-conversation") {
    return (
      <LiveConversationInterface
        question={currentQuestion}
        sessionId={resolvedSessionId ?? sessionId ?? null}
        level={level}
        examType={examType}
        voiceConsentDenied={voiceConsentDenied}
        // Honor the question's prep time when set; otherwise fall back to the
        // 90s thinking window (LCT-002). `prepTime` is always defined (transform
        // defaults it) and preparation_time defaults to 0 in the DB, so a plain
        // `?? 90` would never fire — gate on a positive value instead.
        thinkingSeconds={
          currentQuestion.prepTime && currentQuestion.prepTime > 0
            ? currentQuestion.prepTime
            : 90
        }
        onFinish={handleConversationComplete}
      />
    );
  }

  const shouldShowWaveform =
    mode === "recording" ||
    mode === "section-audio" ||
    (mode === "context-audio" && currentQuestion.contextAudio);
  const isWaveformRecording =
    mode === "recording" ||
    state.contextAudioStatus === "playing" ||
    state.sectionAudioStatus === "playing";
  const showQuestionProgress =
    mode !== "section-audio" && mode !== "context-audio";
  const showTimers = mode !== "section-audio" && mode !== "context-audio";

  return (
    <div
      className={cn(
        "flex h-[100dvh] items-center justify-center bg-muted/50 p-2 transition-all duration-300 sm:p-4",
        state.isFlashing && "bg-exam-recording/20",
      )}
    >
      {/* Dimmed Background Overlay */}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />

      <div
        className={cn(
          "relative z-10 flex max-h-[calc(100dvh-1rem)] w-full max-w-2xl flex-col rounded-2xl border-4 bg-card shadow-2xl transition-all duration-500 sm:max-h-[calc(100dvh-2rem)]",
          getModeColor(mode),
        )}
      >
        {/* Status Ring/Badge */}
        <div className={getModeBadgeClassName(mode)}>
          {getModeLabel(mode, t)}
        </div>

        <div className="custom-scrollbar min-h-0 overflow-y-auto p-3 pt-8 sm:p-8 sm:pt-10">
          {/* Question Progress */}
          {showQuestionProgress && (
            <div className="mb-3 sm:mb-4 flex items-center justify-center gap-2 text-center">
              <span className="text-xs sm:text-sm text-muted-foreground font-ui">
                {questionLabel}
              </span>
              <AdaptiveQuestionStateBadge
                state={currentQuestion.adaptiveState ?? "new"}
              />
            </div>
          )}

          {/* Question Content */}
          <QuestionContent
            mode={mode}
            prompt={currentQuestion.prompt}
            contextText={contextText}
            imageSrc={imageSrc}
          />

          {/* Visualizer */}
          {shouldShowWaveform && (
            <div className="mb-6 bg-muted/30 rounded-xl p-3 sm:p-4">
              <SpeakingWaveform
                isRecording={isWaveformRecording}
                barColor={mode === "recording" ? undefined : "bg-exam-prep"}
                getLevels={readAudioLevels}
              />
            </div>
          )}

          {/* Dual Timers */}
          {showTimers && (
            <TimerDisplay
              mode={mode}
              prepTimeLeft={state.prepTimeLeft}
              recordTimeLeft={state.recordTimeLeft}
              recordedDurationSeconds={state.recordedDurationSeconds}
            />
          )}

          {/* Action Buttons */}
          <ActionButtons
            mode={mode}
            saveStatus={state.saveStatus}
            saveError={state.saveError}
            retryCount={state.retryCount}
            permissionStatus={state.permissionStatus}
            isSaving={state.isSaving}
            voiceConsentDenied={voiceConsentDenied}
            onStartEarly={handleStartEarly}
            onStopRecording={handleStopRecording}
            onRetrySave={handleRetrySave}
          />
        </div>

        {/* Mic Status Indicator */}
        {mode === "recording" && (
          <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 flex items-center gap-1.5 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-exam-recording animate-pulse" />
            <span className="text-[10px] sm:text-xs text-exam-recording font-medium font-ui">
              {t("simulation.speaking.micActive", {
                defaultValue: "Mic Active",
              })}
            </span>
          </div>
        )}
      </div>

      <SimulationInactivityDialog
        open={inactivity.isPromptOpen}
        timeRemainingSeconds={inactivityTimeRemaining}
        onContinue={inactivity.continueSession}
        onEnd={handleFinishModule}
      />
    </div>
  );
}
